import { reactive } from 'vue'
import { api } from '@/api'

// Temps réel (optionnel) : WebSocket au protocole Pusher.
//
// Règle d'or du brief : un événement est un SIGNAL, pas une donnée. Il ne
// contient que des identifiants ; à sa réception on recharge ce qui est
// affiché par l'API habituelle (qui applique organisation et droits). Le
// contenu d'un événement n'est jamais affiché.
//
// Le temps réel peut être désactivé côté serveur (`enabled: false`) ou
// indisponible : l'interrogation périodique (cloche, rapports) reste donc
// toujours en place comme repli.

/** off | connecting | connected | unavailable */
export const realtime = reactive({ status: 'off' })

const listeners = { resource: new Set(), notification: new Set(), report: new Set(), reconnect: new Set() }
let client = null
let generation = 0
let hasConnected = false

export function onRealtime(kind, handler) {
  listeners[kind].add(handler)
  return () => listeners[kind].delete(handler)
}

function emit(kind, payload) {
  listeners[kind].forEach((handler) => {
    try {
      handler(payload)
    } catch (e) {
      console.error('[realtime] handler', e)
    }
  })
}

function debug(...args) {
  if (process.env.NODE_ENV !== 'production') console.debug('[realtime]', ...args)
}

/** Ouvre la connexion si le serveur l'active. À appeler une fois l'utilisateur connecté. */
export async function startRealtime() {
  stopRealtime()
  const mine = ++generation
  realtime.status = 'connecting'

  let config
  try {
    config = await api.realtime.config()
  } catch (e) {
    if (mine === generation) realtime.status = 'unavailable'
    return
  }
  if (mine !== generation) return
  if (!config || !config.enabled) {
    realtime.status = 'off'
    return
  }

  try {
    const { default: Pusher } = await import(/* webpackChunkName: "realtime" */ 'pusher-js')
    if (mine !== generation) return
    const { connection, channels } = config
    const pusher = new Pusher(connection.key, {
      cluster: 'mt1', // exigé par pusher-js, sans effet ici
      wsHost: connection.host,
      wsPort: connection.port,
      wssPort: connection.port,
      forceTLS: connection.scheme === 'https',
      enabledTransports: ['ws', 'wss'],
      disableStats: true
    })
    client = pusher

    pusher.connection.bind('state_change', ({ current }) => {
      if (mine !== generation) return
      realtime.status = current === 'connected' ? 'connected' : current === 'connecting' || current === 'initialized' ? 'connecting' : 'unavailable'
    })
    // Des événements ont pu être manqués pendant une coupure : les écrans rechargent.
    pusher.connection.bind('connected', () => {
      if (hasConnected) emit('reconnect')
      hasConnected = true
    })

    // Événements personnels : tous les rôles.
    if (channels.user) {
      const me = pusher.subscribe(channels.user)
      me.bind('notification.created', (data) => {
        debug('notification.created', data)
        emit('notification', data || {})
      })
      me.bind('report.updated', (data) => {
        debug('report.updated', data)
        emit('report', data || {})
      })
      me.bind('pusher:subscription_error', (status) => debug('abonnement refusé (utilisateur)', status))
    }
    // Données de l'organisation : personnel seulement (canal nul pour propriétaire et chauffeur).
    if (channels.organization) {
      const org = pusher.subscribe(channels.organization)
      org.bind('resource.changed', (data) => {
        debug('resource.changed', data)
        emit('resource', data || {})
      })
      org.bind('pusher:subscription_error', (status) => debug('abonnement refusé (organisation)', status))
    }
  } catch (e) {
    debug('échec de connexion', e)
    if (mine === generation) realtime.status = 'unavailable'
  }
}

/** Ferme la connexion (déconnexion de l'utilisateur, session expirée). */
export function stopRealtime() {
  generation++
  hasConnected = false
  if (client) {
    try {
      client.disconnect()
    } catch (e) {
      /* déjà fermée */
    }
    client = null
  }
  realtime.status = 'off'
}

// ── Correspondance entre un nom de ressource d'événement et un écran ──────────
// Le brief ne fixe pas la forme exacte du champ `resource` (« vehicles »,
// « fuel_log », « expense-categories »…) : on compare des formes normalisées
// (casse, séparateurs et pluriel ignorés).
const normalize = (name) =>
  String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .replace(/ies$/, 'y')
    .replace(/s$/, '')

export function resourceMatches(eventResource, names) {
  const target = normalize(eventResource)
  return names.some((name) => {
    const n = normalize(name)
    return n === target || (n.length >= 4 && target.startsWith(n))
  })
}

/**
 * Abonne un écran au temps réel. Renvoie la fonction de désabonnement (à appeler
 * à la destruction du composant).
 *   resources      : ressources qui concernent cet écran (ex. ['payments', 'vehicles'])
 *   onChange       : recharger la liste / la fiche / la synthèse
 *   onNotification : nouvelle notification ; onReport : rapport mis à jour
 *   onReconnect    : reconnexion après une coupure (tout recharger)
 * Les rafales d'événements sont regroupées (un seul rechargement).
 */
export function subscribeRealtime({ resources = [], onChange, onNotification, onReport, onReconnect, delay = 400 } = {}) {
  const offs = []
  const timers = new Map()
  const debounced = (key, fn, payload) => {
    clearTimeout(timers.get(key))
    timers.set(key, setTimeout(() => fn(payload), delay))
  }

  if (onChange) {
    offs.push(
      onRealtime('resource', (event) => {
        if (resourceMatches(event.resource, resources)) debounced('resource', onChange, event)
      })
    )
    // Après une coupure, on recharge ce qui est affiché.
    offs.push(onRealtime('reconnect', () => debounced('reconnect', onChange, null)))
  }
  if (onReconnect) offs.push(onRealtime('reconnect', () => debounced('reconnect2', onReconnect, null)))
  if (onNotification) offs.push(onRealtime('notification', (event) => debounced('notification', onNotification, event)))
  if (onReport) offs.push(onRealtime('report', (event) => onReport(event)))

  return () => {
    offs.forEach((off) => off())
    timers.forEach((t) => clearTimeout(t))
    timers.clear()
  }
}
